import { UNCHANGED } from '@amatiasq/state-delta';
import type { Delta, Json, Sendable } from '@amatiasq/state-delta';

import { Vector } from './vector.ts';

import { emptyInput } from './entities.ts';
import type { Entity, EntityId, Team } from './entities.ts';
import { BULLET_SYNC_FRAMES } from './parameters.ts';
import type { WeaponName } from './weapons.ts';
import { addEntity, removeEntity } from './world.ts';
import type { World } from './world.ts';

export interface Point {
  x: number;
  y: number;
}

interface SnapshotBase extends Point {
  size: number;
}

interface PlayerSnapshot extends SnapshotBase {
  kind: 'player';
  name: string;
  team: Team;
  aim: Point;
  weapon: WeaponName;
  clip: number;
  reload: number;
  dash: number;
  dashCooldown: number;
}

interface BulletSnapshot extends SnapshotBase {
  kind: 'bullet';
  team: Team;
  ownerId: EntityId;
  velocity: Point;
}

interface ControlPointSnapshot extends SnapshotBase {
  kind: 'control-point';
  ownerTeam: Team | null;
  capturingTeam: Team | null;
  capture: number;
}

type EntitySnapshot = PlayerSnapshot | BulletSnapshot | ControlPointSnapshot;

export type WorldSnapshot = Record<EntityId, EntitySnapshot>;

/** The whole world as plain JSON. Positions are rounded: pixels are integers. */
export function toSnapshot(world: World): WorldSnapshot {
  const snapshot: WorldSnapshot = {};

  for (const entity of world.entities.values()) {
    snapshot[entity.id] = toEntitySnapshot(entity);
  }

  return snapshot;
}

/**
 * The same snapshot with the bullets' movement left out, because the receiver
 * moves them itself. Only bullets it has never seen, and one correction every
 * `BULLET_SYNC_FRAMES` frames, carry a position — and a skipped value costs no
 * bytes at all, unlike sending it again.
 */
export function withSkippedBullets(
  snapshot: WorldSnapshot,
  known: Record<EntityId, unknown>,
  frame: number,
  syncFrames = BULLET_SYNC_FRAMES,
): Record<EntityId, Sendable> {
  const result: Record<EntityId, unknown> = {};

  for (const [id, entity] of Object.entries(snapshot)) {
    const isCorrection = (frame + stagger(id)) % syncFrames === 0;

    result[id] =
      entity.kind !== 'bullet' || isCorrection || !known[id]
        ? entity
        : { ...entity, x: UNCHANGED, y: UNCHANGED };
  }

  return result as Record<EntityId, Sendable>;
}

/**
 * What a delta turned out to be, for the end that wants to hear it. A bullet
 * that was not there a frame ago is somebody's shot, and a reload that has just
 * begun is a sound its neighbours get: both are read off the change that was
 * travelling anyway, and cost no bytes of their own.
 */
export interface WorldChanges {
  /** Entities this delta brought into the world, in the order they arrived. */
  created: EntityId[];
  /** Players whose reload started with this delta. */
  reloaded: EntityId[];
}

/**
 * Brings the world in line with what the server said. It applies the *delta*,
 * not the picture: a value the server chose to skip is missing from the delta,
 * and the receiver keeps the one it worked out itself. Applying the whole
 * picture instead would drag every bullet back to where it was last corrected.
 *
 * The local player's position is left alone, because it is not this function's
 * to decide: the client has already put it where the server said and replayed
 * what the server had not seen (`src/client/prediction.ts`).
 */
export function applyDelta(
  world: World,
  known: WorldSnapshot,
  delta: Delta,
  localPlayerId?: EntityId,
): WorldChanges {
  const changes: WorldChanges = { created: [], reloaded: [] };

  if (!isObject(delta)) return changes;

  const lead = predictionLead();

  for (const [id, change] of Object.entries(delta)) {
    if (Array.isArray(change)) {
      if (change.length === 0) removeEntity(world, id);
      else replaceEntity(id);
      continue;
    }

    if (!world.entities.has(id)) {
      replaceEntity(id);
      continue;
    }

    updateEntity(id, Object.keys(change ?? {}));
  }

  return changes;

  /**
   * How far ahead of the server this end believes its own player to be — which
   * is the whole point of prediction, and the reason a shot of ours needs
   * correcting below. Since the correction is a replay, this is exactly the
   * ground the replay covered: nothing to trust, nothing to threshold.
   */
  function predictionLead() {
    const mine = localPlayerId ? world.entities.get(localPlayerId) : undefined;
    const said = localPlayerId ? known[localPlayerId] : undefined;

    if (!mine || !said) return Vector.of(0, 0);

    return mine.position.sub(Vector.of(said.x, said.y));
  }

  function replaceEntity(id: EntityId) {
    const snapshot = known[id];

    if (!snapshot) return;

    const previous = world.entities.get(id);
    const entity = fromEntitySnapshot(id, snapshot);

    if (!previous) {
      startAtOurMuzzle(entity);
      changes.created.push(id);
    }

    keepWhatIsOurs(entity, previous);
    addEntity(world, entity);
  }

  function updateEntity(id: EntityId, changed: string[]) {
    const snapshot = known[id];
    const current = world.entities.get(id);

    if (!snapshot || !current) return;

    const entity = fromEntitySnapshot(id, snapshot);

    // A reload is a state on the wire, but its *start* is an event, and this is
    // the only line that still holds the old value next to the new one.
    if (
      current.kind === 'player' &&
      entity.kind === 'player' &&
      current.reload === 0 &&
      entity.reload > 0
    ) {
      changes.reloaded.push(id);
    }

    // The fields the sender is allowed to skip: the receiver has been moving the
    // bullet itself, and its own answer is fresher than the last one sent.
    if (!changed.includes('x') && !changed.includes('y')) {
      entity.position = current.position;
    }

    keepWhatIsOurs(entity, current);
    Object.assign(current, entity);
  }

  /**
   * A shot of ours leaves the barrel the *server* saw, which is a round trip
   * behind the one this client is drawing: run to the right and your own
   * bullets come out of your back. The correction is the disagreement itself,
   * so the bullet lands exactly on the muzzle without anybody guessing at the
   * latency.
   *
   * What it does not touch is the ground the bullet has already covered — it
   * was fired a frame ago and it really is that far out.
   */
  function startAtOurMuzzle(entity: Entity) {
    if (entity.kind !== 'bullet' || entity.ownerId !== localPlayerId) return;

    entity.position = entity.position.add(lead);
  }

  /** Input and prediction belong to this end, and no snapshot may erase them. */
  function keepWhatIsOurs(entity: Entity, previous: Entity | undefined) {
    if (!previous) return;

    // Our own bullet was born a lead ahead of where the server fired it, and
    // both ends fly it down the same straight line from there. The periodic
    // correction has nothing to fix and would only drag it back behind us.
    if (entity.kind === 'bullet' && entity.ownerId === localPlayerId) {
      entity.position = previous.position;
      return;
    }

    const isLocalPlayer = entity.id === localPlayerId;

    if (entity.kind === 'player' && previous.kind === 'player') {
      entity.input = previous.input;

      // The dash countdown is prediction too: this end started it and counts
      // the same milliseconds down. A snapshot still in flight when it began
      // would cancel it and hand it back two frames later, which reads as a
      // stutter.
      if (isLocalPlayer) {
        entity.dash = previous.dash;
        entity.dashCooldown = previous.dashCooldown;
      }
    }

    // Our own position was settled before this delta was walked, by planting
    // it on the server's word and replaying the frames it had not seen. This
    // snapshot is the thing that was already taken into account.
    if (isLocalPlayer) entity.position = previous.position;
  }
}

function isObject(value: unknown): value is Record<string, Json> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toEntitySnapshot(entity: Entity): EntitySnapshot {
  const base = {
    x: round(entity.position.x),
    y: round(entity.position.y),
    size: round(entity.size),
  };

  if (entity.kind === 'player') {
    return {
      ...base,
      kind: 'player',
      name: entity.name,
      team: entity.team,
      aim: { x: round(entity.aim.x), y: round(entity.aim.y) },
      weapon: entity.weapon,
      clip: entity.clip,
      reload: Math.round(entity.reload),
      dash: Math.round(entity.dash),
      dashCooldown: Math.round(entity.dashCooldown),
    };
  }

  if (entity.kind === 'bullet') {
    return {
      ...base,
      kind: 'bullet',
      team: entity.team,
      ownerId: entity.ownerId,
      velocity: { x: round(entity.velocity.x), y: round(entity.velocity.y) },
    };
  }

  return {
    ...base,
    kind: 'control-point',
    ownerTeam: entity.ownerTeam,
    capturingTeam: entity.capturingTeam,
    capture: entity.capture,
  };
}

function fromEntitySnapshot(id: EntityId, snapshot: EntitySnapshot): Entity {
  const base = {
    id,
    position: Vector.of(snapshot.x, snapshot.y),
    size: snapshot.size,
  };

  if (snapshot.kind === 'player') {
    return {
      ...base,
      kind: 'player',
      name: snapshot.name,
      team: snapshot.team,
      aim: Vector.of(snapshot.aim.x, snapshot.aim.y),
      weapon: snapshot.weapon,
      clip: snapshot.clip,
      cooldown: 0,
      reload: snapshot.reload,
      dash: snapshot.dash,
      dashCooldown: snapshot.dashCooldown,
      input: emptyInput(),
    };
  }

  if (snapshot.kind === 'bullet') {
    return {
      ...base,
      kind: 'bullet',
      team: snapshot.team,
      ownerId: snapshot.ownerId,
      velocity: Vector.of(snapshot.velocity.x, snapshot.velocity.y),
      // Only the server counts a bullet's life down; here it dies when told.
      range: Number.POSITIVE_INFINITY,
    };
  }

  return {
    ...base,
    kind: 'control-point',
    ownerTeam: snapshot.ownerTeam,
    capturingTeam: snapshot.capturingTeam,
    capture: snapshot.capture,
  };
}

/** Spreads the corrections over the frames instead of bunching them up. */
function stagger(id: EntityId) {
  return id.charCodeAt(id.length - 1);
}

function round(value: number) {
  return Math.round(value * 10) / 10;
}
